import React, { useMemo } from 'react';
import type { ScheduleProjectSummary } from '../../../../types/workflows';
import type { MilestoneRow } from '../../../../AppSettings/services/MilestoneService';
import { getStatusColorValue, getDisciplineColor, getOwnerColor } from '../Timeline/ScheduleTimeline';
import './ScheduleLegend.css';

export interface ScheduleLegendProps {
  projects: ScheduleProjectSummary[];
  colorMode: 'status' | 'discipline' | 'owner';
  onColorModeChange?: (mode: 'status' | 'discipline' | 'owner') => void;
  milestonesByProject?: Record<string, MilestoneRow[]>;
}

interface LegendItem {
  color: string;
  label: string;
  count: number;
  order: number;
}

/**
 * Schedule Legend Component
 * 
 * Displays color mappings for the current color mode and allows toggling between modes.
 */
const ScheduleLegend: React.FC<ScheduleLegendProps> = ({
  projects,
  colorMode,
  onColorModeChange,
  milestonesByProject,
}) => {
  // Generate legend items based on color mode
  const legendItems = useMemo(() => {
    const items: Map<string, LegendItem> = new Map();
    let orderCounter = 0;

    const hasMilestones =
      colorMode === 'status' &&
      milestonesByProject &&
      Object.values(milestonesByProject).some((list) => (list?.length || 0) > 0);

    if (hasMilestones) {
      // Build legend from milestone statuses so colors align with milestone bars
      projects.forEach((project) => {
        const list = milestonesByProject?.[project.projectId] || [];
        list.forEach((m) => {
          const status = (m.status || '').trim() || 'Unknown';
          const color = getStatusColorValue(status);
          const key = `${color}-${status}`;
          if (items.has(key)) {
            items.get(key)!.count += 1;
          } else {
            items.set(key, { color, label: status, count: 1, order: orderCounter++ });
          }
        });
      });
    } else {
      // Default: build legend from projects
      projects.forEach((project) => {
        let color: string;
        let label: string;

        switch (colorMode) {
          case 'status': {
            color = getStatusColorValue(project.projectStatus);
            label = project.projectStatus || 'Unknown';
            break;
          }
          case 'discipline': {
            const discipline = project.disciplines?.[0] || 'Unknown';
            color = getDisciplineColor(discipline);
            label = discipline;
            break;
          }
          case 'owner': {
            const ownerName = project.ownerName || project.ownerId || 'Unknown';
            color = getOwnerColor(project.ownerId);
            label = ownerName;
            break;
          }
          default:
            color = '#9e9e9e';
            label = 'Unknown';
        }

        const key = `${color}-${label}`;
        if (items.has(key)) {
          items.get(key)!.count += 1;
        } else {
          items.set(key, { color, label, count: 1, order: orderCounter++ });
        }
      });
    }

    // Sort by first-seen order (matches lane ordering), then label
    return Array.from(items.values()).sort((a, b) => {
      if (a.order !== b.order) return a.order - b.order;
      return a.label.localeCompare(b.label);
    });
  }, [projects, colorMode, milestonesByProject]);

  // Get color mode display name
  const colorModeLabel = useMemo(() => {
    switch (colorMode) {
      case 'status':
        return 'Status';
      case 'discipline':
        return 'Discipline';
      case 'owner':
        return 'Owner';
      default:
        return 'Unknown';
    }
  }, [colorMode]);

  return (
    <div className="schedule-legend">
      <div className="legend-header">
        <h3 className="legend-title">Color Legend</h3>
        <div className="legend-items">
          {legendItems.length === 0 ? (
            <span className="legend-empty-inline">No data to display</span>
          ) : (
            legendItems.map((item, index) => (
              <div key={`${item.color}-${item.label}-${index}`} className="legend-item">
                <div
                  className="legend-color-swatch"
                  style={{ backgroundColor: item.color }}
                  aria-label={`Color: ${item.color}`}
                />
                <span className="legend-label">{item.label}</span>
                <span className="legend-count">({item.count})</span>
              </div>
            ))
          )}
        </div>
        {onColorModeChange && (
          <div className="legend-mode-toggle">
            <span className="legend-mode-label">Color by:</span>
            <button
              className={`legend-mode-btn ${colorMode === 'status' ? 'active' : ''}`}
              onClick={() => onColorModeChange('status')}
              aria-label="Color by status"
            >
              Status
            </button>
            <button
              className={`legend-mode-btn ${colorMode === 'discipline' ? 'active' : ''}`}
              onClick={() => onColorModeChange('discipline')}
              aria-label="Color by discipline"
            >
              Discipline
            </button>
            <button
              className={`legend-mode-btn ${colorMode === 'owner' ? 'active' : ''}`}
              onClick={() => onColorModeChange('owner')}
              aria-label="Color by owner"
            >
              Owner
            </button>
          </div>
        )}
      </div>

      {/* Status mode specific help text - kept below the single line */}
      {colorMode === 'status' && (
        <div className="legend-help">
          <p className="legend-help-text">
            <strong>Status Colors:</strong> Green = Completed, Red = Overdue, Yellow = In Progress, Grey = Planned
          </p>
        </div>
      )}
    </div>
  );
};

export default ScheduleLegend;

